"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.writeHeader = writeHeader;
const takeoff_layout_ts_1 = require("./takeoff-layout.js");
const takeoff_sheet_ts_1 = require("./takeoff-sheet.js");
const FORMULAS = 'Formulas';
/** The template's branch + labour lists (Formulas sheet; I3's lookup is A63:C133, row 63 the "select" prompt) and the label for typed rates. */
const LABOUR_LIST = { first: 64, last: 133 };
const ABSEIL_LIST = { first: 249, last: 265 };
const SPECIAL_RATE = 'SPECIAL RATE';
/** Excel's day 0, as a Unix day number (1899-12-30). */
const EXCEL_EPOCH_DAYS = 25569;
const MS_PER_DAY = 86_400_000;
/** The client and project details (B3:E23); the template's cells, as TAKEOFF-LAYOUT.md §2 lists them. */
const DETAILS = { clientName: 'B3', clientAddress: 'B4', project: 'B13', date: 'B20', estimator: 'B21', jobNumber: 'B23' };
/** The template's row for this branch and labour type ("GOLD COAST wages"), by the branch's name; null when it has none. */
function listRow(template, list, wanted) {
    for (let r = list.first; r <= list.last; r++)
        if (wanted((0, takeoff_sheet_ts_1.squash)(template.text(FORMULAS, `A${r}`))))
            return r;
    return null;
}
function headerRates(sheet, template, rates, job) {
    const branch = (0, takeoff_sheet_ts_1.squash)(job.branch ?? '');
    const type = (0, takeoff_sheet_ts_1.squash)(job.labourType);
    // "GOLD COAST wages"; the list shortens some names ("SUNSHINE" for Sunshine Coast).
    const exact = listRow(template, LABOUR_LIST, (e) => e === `${branch}${type}`);
    const row = exact ?? (branch ? listRow(template, LABOUR_LIST, (e) => e.endsWith(type) && branch.startsWith(e.slice(0, -type.length))) : null);
    sheet.text(takeoff_layout_ts_1.HEADER.branchLabour, row === null ? SPECIAL_RATE : template.text(FORMULAS, `A${row}`));
    const lookup = (col) => (row === null ? null : (template.num(FORMULAS, `${col}${row}`) ?? 0));
    // The list's rate stays a lookup where it is the app's; where it isn't, it is typed over.
    const pairs = [
        [takeoff_layout_ts_1.HEADER.labourSell, rates.labour.sell, 'B'],
        [takeoff_layout_ts_1.HEADER.labourCost, rates.labour.cost, 'C'],
        [takeoff_layout_ts_1.HEADER.overtimeSell, rates.overtime.sell, 'D'],
        [takeoff_layout_ts_1.HEADER.overtimeCost, rates.overtime.cost, 'E'],
        [takeoff_layout_ts_1.HEADER.foremanSell, rates.foreman.sell, 'F'],
        [takeoff_layout_ts_1.HEADER.foremanCost, rates.foreman.cost, 'G'],
    ];
    for (const [address, value, col] of pairs)
        if (!(0, takeoff_sheet_ts_1.same)(value, lookup(col)))
            sheet.cells.set(address, value);
    const abseil = branch ? listRow(template, ABSEIL_LIST, (e) => e === branch) : null;
    if (abseil !== null)
        sheet.text(takeoff_layout_ts_1.HEADER.abseilBranch, template.text(FORMULAS, `A${abseil}`));
    const abseilLookup = (col) => (abseil === null ? 0 : (template.num(FORMULAS, `${col}${abseil}`) ?? 0));
    if (!(0, takeoff_sheet_ts_1.same)(rates.abseil.sell, abseilLookup('B')))
        sheet.cells.set(takeoff_layout_ts_1.HEADER.abseilSell, rates.abseil.sell);
    if (!(0, takeoff_sheet_ts_1.same)(rates.abseil.cost, abseilLookup('C')))
        sheet.cells.set(takeoff_layout_ts_1.HEADER.abseilCost, rates.abseil.cost);
    sheet.number(takeoff_layout_ts_1.HEADER.allowanceSell, rates.allowance.sell);
    sheet.number(takeoff_layout_ts_1.HEADER.allowanceCost, rates.allowance.cost);
    // Every block's wash row divides by it, used or not.
    sheet.number(takeoff_layout_ts_1.HEADER.washRate, (0, takeoff_sheet_ts_1.perHour)(rates.washRate));
    sheet.number(takeoff_layout_ts_1.HEADER.markupMaterials, rates.markupMaterials);
    sheet.number(takeoff_layout_ts_1.HEADER.markupOther, rates.markupOther);
    sheet.number(takeoff_layout_ts_1.HEADER.consumables, rates.consumablesPerLitre);
    sheet.number(takeoff_layout_ts_1.HEADER.negotiatingFactor, rates.negotiatingFactor);
}
/** Each slot's paint: its name on the label cell and its cost per litre. */
function headerPaints(sheet, rates) {
    for (const [id, slot] of sheet.slots) {
        const paint = rates.paints.find((p) => p.id === id);
        if (!paint)
            continue;
        const cells = takeoff_layout_ts_1.PAINT_SLOTS[slot];
        // The label cells end with a colon ("Standard Paint:"), as the template has them.
        sheet.text(cells.label, `${paint.name}:`);
        sheet.number(cells.cost, paint.costPerLitre);
    }
}
/** Excel's day number for a YYYY-MM-DD date. */
function excelDate(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return Date.UTC(y, m - 1, d) / MS_PER_DAY + EXCEL_EPOCH_DAYS;
}
function headerDetails(sheet, job) {
    sheet.text(DETAILS.clientName, job.client.name);
    sheet.text(DETAILS.clientAddress, job.client.address.replace(/\s*\n\s*/g, ', '));
    sheet.text(DETAILS.project, [job.jobNumber, job.jobTitle].filter(Boolean).join(' '));
    sheet.cells.set(DETAILS.date, excelDate(job.date));
    sheet.text(DETAILS.estimator, job.estimator);
    sheet.text(DETAILS.jobNumber, job.jobNumber);
}
/** The whole header, once a workbook's areas (and so its paint slots) are placed. */
function writeHeader(sheet, template, rates, job) {
    headerRates(sheet, template, rates, job);
    headerPaints(sheet, rates);
    headerDetails(sheet, job);
}
